import React, { useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Layers, Sparkles, Wand2 } from "lucide-react";
import Header from "../../components/Header";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { useConfig } from "../../hooks/useApi";
import { cn } from "../../lib/cn";
import { GenerationStreamProvider } from "../../hooks/useAdminStream";

const navItems = [
  { to: "/admin/releases", label: "Releases", icon: Layers },
  { to: "/admin/generate", label: "Gerar", icon: Wand2 },
  { to: "/admin/ai", label: "IA", icon: Sparkles },
];

export const AdminLayout: React.FC = () => {
  const { logout } = useAdminAuth();
  const { data: config } = useConfig();
  const brandName = config?.branding?.name || "Changelog";

  useEffect(() => {
    document.title = `Admin · ${brandName}`;
    const setMeta = (name: string, content: string) => {
      let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      let created = false;
      if (!tag) {
        tag = document.createElement("meta");
        tag.name = name;
        document.head.appendChild(tag);
        created = true;
      }
      const previous = tag.content;
      tag.content = content;
      return () => {
        if (created && tag) tag.remove();
        else if (tag) tag.content = previous;
      };
    };
    const cleanups = [
      setMeta("robots", "noindex, nofollow, noarchive, nosnippet"),
      setMeta("googlebot", "noindex, nofollow"),
    ];
    return () => cleanups.forEach((fn) => fn());
  }, [brandName]);

  return (
    <div className="min-h-screen flex flex-col text-foreground">
      <a href="#admin-main" className="skip-link">
        Pular para o conteúdo
      </a>
      <Header showActions={false} onLogout={logout} />

      <div className="flex-1 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-8 grid grid-cols-1 md:grid-cols-[15rem_1fr] gap-6 md:gap-12">
        <aside className="md:sticky md:top-20 md:self-start">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium mb-3 px-2">
            Painel
          </p>
          <nav className="flex md:flex-col gap-1 overflow-x-auto -mx-1 px-1" aria-label="Navegação do admin">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap relative",
                    isActive
                      ? "bg-primary/10 text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-primary"
                      />
                    )}
                    <Icon
                      className={cn(
                        "w-4 h-4 transition-colors",
                        isActive ? "text-primary" : ""
                      )}
                      aria-hidden="true"
                    />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main id="admin-main" tabIndex={-1} className="min-w-0">
          <GenerationStreamProvider>
            <Outlet />
          </GenerationStreamProvider>
        </main>
      </div>
    </div>
  );
};
